import { Box, Button, Dialog, DialogActions, DialogTitle } from "@mui/material";
import { useState, type FC } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useSupabaseCallback } from "../../server/supabase/hooks";
import { SupabaseTagsService } from "./server/supabase-tags-service";
import type { Tag } from "./types";

export const TagDeleteDialog: FC<{
  open: boolean;
  handleClose: () => void;
  tag?: Tag;
  reload: () => void;
}> = ({ open, handleClose, tag, reload }) => {
  const { t } = useTranslation();
  const [saving, setSaving] = useState(false);

  const handleDelete = useSupabaseCallback(
    async ({ supabase }) => {
      if (saving || !tag) return;
      setSaving(true);

      const { error } = await new SupabaseTagsService(supabase).deleteTag(tag.id);
      setSaving(false);

      if (error) {
        toast.error(t("Deleting tag failed, please try again"));
        return;
      }

      toast.success(t("Tag deleted"));
      reload();
      handleClose();
    },
    [handleClose, reload, saving, t, tag],
  );

  const onDeleteClick = (): void => {
    void handleDelete();
  };

  if (!tag) return null;

  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogTitle>
        <Box>{t(`Delete ${tag.name} Tag`)}</Box>
      </DialogTitle>
      <DialogActions>
        <Button onClick={handleClose} data-analytics-id="delete-tag-cancel" variant="text">
          {t("Cancel")}
        </Button>
        <Button
          onClick={onDeleteClick}
          data-analytics-id="delete-tag"
          variant="text"
          loading={saving}
          disabled={saving}
        >
          {t("Delete")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
